import type { PageQuery } from "@/domain/port/PageQuery";
import type { HttpClient } from "@/infrastructure/HttpClient";
import type { JobService } from "@/domain/port/JobService";
import type { CreateJobCommand, JobDto } from "./dto/JobDto";
import {
  jobFromDto,
  jobIntakeRequestFromCommand,
  jobUpdateRequestFromCommand,
} from "./mappers/JobMapper";
import type {
  CreateJobIntakeCommand,
  Job,
  UpdateJobCommand,
} from "@/domain/model/Job";
import type { JobId } from "@/domain/model/Ids";

export class JobServiceImpl implements JobService {
  constructor(private readonly http: HttpClient) {}

  async list(q: PageQuery): Promise<{ items: Job[]; total: number }> {
    const params = toQueryParams(q);

    const { items, total } = await this.http.request<{
      items: JobDto[];
      total: number;
    }>(`/api/jobs?${params.toString()}`);

    return { items: items.map(jobFromDto), total };
  }

  async create(cmd: CreateJobCommand): Promise<Job> {
    const res = await this.http.request<JobDto>("/api/jobs", {
      method: "POST",
      body: JSON.stringify(cmd),
    });

    return jobFromDto(res);
  }

  async getById(id: JobId): Promise<Job> {
    const dto = await this.http.request<JobDto>(
      `/api/jobs/${encodeURIComponent(String(id))}`,
    );
    return jobFromDto(dto);
  }

  async update(id: JobId, cmd: UpdateJobCommand): Promise<Job> {
    const req = jobUpdateRequestFromCommand(cmd);
    const dto = await this.http.request<JobDto>(
      `/api/jobs/${encodeURIComponent(String(id))}`,
      {
        method: "PUT",
        body: JSON.stringify(req),
      },
    );
    return jobFromDto(dto);
  }

  async delete(id: JobId): Promise<Job> {
    const dto = await this.http.request<JobDto>(
      `/api/jobs/${encodeURIComponent(String(id))}`,
      { method: "DELETE" },
    );
    return jobFromDto(dto);
  }

  async createIntake(cmd: CreateJobIntakeCommand): Promise<Job> {
    const res = await this.http.request<JobDto>("/api/jobs/intake", {
      method: "POST",
      body: JSON.stringify(jobIntakeRequestFromCommand(cmd)),
    });

    return jobFromDto(res);
  }
}

function isNonEmptyString(x: unknown): x is string {
  return typeof x === "string" && x.trim().length > 0;
}

function flattenJobFilter(filter: any): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};

  if (isNonEmptyString(filter?.q)) out.q = filter.q.trim();

  // Multi-state
  if (Array.isArray(filter?.state) && filter.state.length > 0) {
    out.state = filter.state.map(String);
  }

  if (isNonEmptyString(filter?.plannedFrom))
    out.plannedFrom = filter.plannedFrom;
  if (isNonEmptyString(filter?.plannedTo)) out.plannedTo = filter.plannedTo;

  if (isNonEmptyString(filter?.constructionSiteId))
    out.constructionSiteId = filter.constructionSiteId;
  if (isNonEmptyString(filter?.assignedPersonId))
    out.assignedPersonId = filter.assignedPersonId;

  return out;
}

function toQueryParams(q: PageQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(q.page),
    pageSize: String(q.pageSize),
    sortBy: q.sortBy ?? "createdAt",
    sortDir: q.sortDir ?? "desc",
  });

  const flat = flattenJobFilter(q.filter);

  for (const [key, value] of Object.entries(flat)) {
    if (Array.isArray(value)) {
      // repeated params, e.g. state=OPEN&state=IN_PROGRESS
      value.forEach((v) => params.append(key, v));
    } else {
      params.set(key, value);
    }
  }

  return params;
}
